/**
 * useMyPermissions — the caller's own permission codes, for navigation gating.
 *
 * WHY IT IS FETCHED AND NOT DERIVED FROM THE ROLE CODE
 * ---------------------------------------------------
 * The frontend already knows `role_code`, so a lookup table mapping role → permissions
 * looks cheaper. It would be a SECOND source of truth for a fact the database owns, and it
 * is already stale: measured on 2026-08-27, `worker` carries FOUR `processes.*` permissions
 * in the live `role_permissions` rows against ONE in the backend's own `DEFAULT_ROLES`,
 * because the seed adds to them. A table shipped today would have been wrong today.
 *
 * WHAT IT IS FOR, AND WHAT IT IS NOT FOR
 * --------------------------------------
 * UX only, exactly like `useLicensedModules`. The backend gates every endpoint with
 * `require_permission`, so this hook decides what is worth SHOWING, never what is allowed.
 * A stale or failed fetch must therefore never open a door — and it cannot, because there is
 * no door here.
 *
 * FAILING OPEN IS THE CORRECT DIRECTION, and it is the opposite of the usual rule
 * (session-discipline §12 asks for the safe direction; here "safe" is the permissive one).
 * If the fetch fails we show the full navigation: the user then meets the server's 403,
 * which is the honest answer. Hiding navigation on a network blip would make the product
 * look like it had silently removed features, and would do it to users who DO have access.
 *
 * The `*` wildcard is reported as `isSuperuser` rather than expanded, mirroring
 * `require_permission`'s own short-circuit so the two cannot drift.
 */
import { useCallback, useEffect, useState } from 'react';

import { systemApi } from '../api';

export interface MyPermissions {
  /** Explicit codes. Empty for a superuser, whose access comes from the flag. */
  codes: Set<string>;
  isSuperuser: boolean;
  /** False until the first fetch settles. Callers must not hide anything while false. */
  resolved: boolean;
  /**
   * Whether the caller holds a permission. True for a superuser, and true for EVERYONE
   * while unresolved — see the fail-open note above.
   */
  can: (code: string) => boolean;
  /** True when the caller holds at least one of `codes`. */
  canAny: (...codes: string[]) => boolean;
}

export function useMyPermissions(): MyPermissions {
  const [codes, setCodes] = useState<Set<string>>(new Set());
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await systemApi.myPermissionsApi.get();
        if (cancelled) return;
        setCodes(new Set(data.permissions ?? []));
        setIsSuperuser(!!data.is_superuser);
      } catch {
        // Deliberately leaves `codes` empty AND flips `resolved` — with `can()` failing
        // open, that shows everything, which is what we want on a failure.
        if (!cancelled) setIsSuperuser(false);
      } finally {
        if (!cancelled) setResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const can = useCallback(
    (code: string) => {
      if (!resolved) return true;
      if (isSuperuser) return true;
      // A failed fetch leaves an empty set on a resolved state. Treating that as "no
      // permissions" would blank the navigation for a user who has them.
      if (codes.size === 0) return true;
      return codes.has(code);
    },
    [codes, isSuperuser, resolved],
  );

  const canAny = useCallback(
    (...wanted: string[]) => wanted.some((code) => can(code)),
    [can],
  );

  return { codes, isSuperuser, resolved, can, canAny };
}
